import { defineStore } from 'pinia';
import { useRoute } from 'vue-router';
import type { identifier } from '@core/models/base';
import type { FeaturesDisplayModel } from '@core/models/features';
import { availableFeaturesFromStudioId, availableFeaturesFromStudioSlug } from '@shared/services/features.service';
import { useOptionalRouteIds } from '@shared/composables/useRouteIds';

export interface FeaturesState {
  initialized: boolean;
  loading: boolean;
  studioId: identifier;
  studioSlug: string | undefined;
  features: FeaturesDisplayModel[];
}

export type FeatureIdentifier =
  | 'rspoJob'
  | 'pkgmgmt'
  | 'subjectManagement'
  | 'bulkDeleteEmptySubjects'
  | 'yearbook'
  | 'studioAccessToken'
  | 'studioVerification'
  | 'markerio'
  | 'serviceItemExport'
  | 'singleAdminSession';

export const useFeaturesStore = defineStore('features', {
  state: (): FeaturesState => ({
    initialized: false,
    loading: false,
    studioId: 0,
    studioSlug: '',
    features: [],
  }),

  getters: {
    featureIdentifiers(state) {
      return new Set(state.features.map((feature) => feature.identifier));
    },
  },

  actions: {
    async initialize() {
      const route = useRoute();
      const { studioSlug } = route.params;
      const [studioId] = useOptionalRouteIds('studioId');

      const studioSlugString = typeof studioSlug === 'string' ? studioSlug : '';
      const normalizedStudioId = studioId ?? 0; // Normalize null to 0 for comparison

      // Check if studio context has changed
      const studioChanged = studioSlugString !== this.studioSlug || normalizedStudioId !== this.studioId;

      if (studioChanged) {
        // Reset initialization flag for new studio
        this.initialized = false;
      }

      // Early return if already initialized (and studio hasn't changed)
      if (this.initialized) {
        return;
      }

      try {
        this.loading = true;
        if (studioSlugString) {
          this.studioSlug = studioSlugString;
          this.studioId = 0; // Clear studioId when using slug
          this.features = await availableFeaturesFromStudioSlug(studioSlugString);
        } else if (studioId != null) {
          this.studioId = studioId;
          this.studioSlug = ''; // Clear slug when using studioId
          this.features = await availableFeaturesFromStudioId(studioId);
        }
        this.initialized = true;
      } finally {
        this.loading = false;
      }
    },

    async initializeWithStudioId(studioId: identifier) {
      if (studioId !== this.studioId) {
        this.loading = true;
        this.studioId = studioId;
        this.features = await availableFeaturesFromStudioId(studioId);
        this.loading = false;
        this.initialized = true;
      }
    },

    async refresh() {
      try {
        this.loading = true;
        if (this.studioSlug) {
          this.features = await availableFeaturesFromStudioSlug(this.studioSlug);
        } else if (this.studioId) {
          this.features = await availableFeaturesFromStudioId(this.studioId);
        }
      } finally {
        this.loading = false;
      }
    },

    hasFeature(featureId: FeatureIdentifier) {
      return this.featureIdentifiers.has(featureId);
    },

    clear() {
      this.studioId = 0;
      this.studioSlug = '';
      this.features = [];
    },
  },
});
